import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, MinusCircle } from "lucide-react";

interface PlanDisplayProps {
  isPremium?: boolean;
  isBasic?: boolean;
}

const PlanDisplay: React.FC<PlanDisplayProps> = ({ isPremium = false, isBasic = false }) => {
  const features = [
    { name: "Gallery images", free: "0", premium: "10+" },
    { name: "Phone number visible", free: false, premium: true },
    { name: "Business logo on card", free: true, premium: true },
    { name: "Cover image", free: true, premium: true },
    { name: "Service listings", free: "2 Max", premium: "Unlimited" },
    { name: "Tags & filters", free: "Limited", premium: "Full control" },
    { name: "Google reviews", free: false, premium: "Rating + Link" },
    { name: "Directory card", free: "No profile pic", premium: "Image & CTA" },
    { name: "Analytics", free: false, premium: "Full insights" },
    { name: "Booking link", free: true, premium: true }
  ];

  // Get current plan name
  const currentPlan = isPremium ? "Premium" : "Free";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle>Your Plan</CardTitle>
          {isPremium ? (
            <Badge className="bg-sauna-orange text-white">Premium</Badge>
          ) : isBasic ? (
            <Badge className="bg-sauna-blue text-white">Basic</Badge>
          ) : (
            <Badge variant="outline" className="text-gray-600 border-gray-400">Free</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!isPremium && !isBasic && (
            <div className="bg-gradient-to-r from-sauna-orange/10 to-sauna-blue/10 p-4 rounded-md border border-sauna-orange/20">
              <h3 className="font-medium text-sauna-navy mb-1">Free Plan Features</h3>
              <p className="text-sm text-gray-600">
                You're currently on our Free plan. Upgrade to get more visibility and features!
              </p>
            </div>
          )}
          
          {isBasic && !isPremium && (
            <div className="bg-gradient-to-r from-sauna-blue/10 to-sauna-blue/5 p-4 rounded-md border border-sauna-blue/20">
              <h3 className="font-medium text-sauna-navy mb-1">Basic Plan Features</h3>
              <p className="text-sm text-gray-600">
                You're on our Basic plan. Upgrade to Premium for maximum exposure!
              </p>
            </div>
          )}
          
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-4 mb-2 border-b pb-2">
              <span className="font-medium text-sm text-gray-600">Feature</span>
              <span className="text-center text-xs font-medium text-gray-500">Free (€0/mo)</span>
              <span className="text-center text-xs font-medium text-gray-500">Premium (€49/mo)</span>
            </div>
            
            {features.map((feature, index) => (
              <div key={index} className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100 items-center">
                <span className="text-sm text-gray-700">{feature.name}</span>
                
                {/* Free plan */}
                <div className="flex justify-center">
                  {typeof feature.free === 'boolean' ? (
                    feature.free ? 
                      <CheckCircle size={18} className="text-green-500" /> : 
                      <XCircle size={18} className="text-gray-300" />
                  ) : feature.free === "Limited" || feature.free === "No profile pic" ? (
                    <MinusCircle size={18} className="text-amber-400" />
                  ) : (
                    <span className="text-sm text-center">{feature.free}</span>
                  )}
                </div>
                
                {/* Premium plan */}
                <div className="flex justify-center">
                  {typeof feature.premium === 'boolean' ? (
                    feature.premium ? 
                      <CheckCircle size={18} className="text-green-500" /> : 
                      <XCircle size={18} className="text-gray-300" />
                  ) : (
                    <span className="text-sm font-medium text-center">{feature.premium}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-end">
        {currentPlan !== "Premium" && (
          <Button className="bg-gradient-to-r from-sauna-orange to-sauna-blue text-white hover:opacity-90">
            Upgrade to Premium
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default PlanDisplay;
